<template>
  <div class="ev-table-legend">
    <div v-if="!details">
      <div class="ev-legend-header">
        <h4 class="ev-legend-title">Legenda prikaza</h4>
        <span class="ev-legend-hint">
          💡 Kliknite na red za detalje o zaposlenom
        </span>
      </div>
      <div class="ev-legend-items">
        <span class="ev-legend-item">
          <span class="ev-legend-sample">5</span>
          iskorišćeni godišnji
        </span>
        <span class="ev-legend-item">
          <span class="ev-legend-sample ev-planned-value">(3)</span>
          planirani godišnji
        </span>
        <span class="ev-legend-item">
          <span class="ev-legend-sample">5</span>
          <span class="ev-legend-sample ev-planned-value">(3)</span>
          tekući mesec: iskorišćeno + planirano
        </span>
      </div>
    </div>

    <div v-else class="ev-legend-details">
      <div class="ev-legend-header">
        <h4 class="ev-legend-title">👤 {{ details.name }}</h4>
        <button
          type="button"
          class="ev-legend-clear"
          @click="$emit('clear-selection')"
        >
          ✕ Ukloni izbor
        </button>
      </div>

      <div class="ev-details-grid">
        <div class="ev-details-card">
          <div class="ev-details-card-title">📥 Iz prethodne godine</div>
          <div class="ev-details-row">
            <span>Preneseno (do 30.06):</span>
            <strong>{{ details.lastYearFirstHalf }}</strong>
          </div>
          <div class="ev-details-row">
            <span>Preneseno (01.07-31.12):</span>
            <strong>{{ details.lastYearSecondHalf }}</strong>
          </div>
          <div class="ev-details-row">
            <span>Raspoloživo iz prošle godine:</span>
            <strong>{{ details.availableLastYear }}</strong>
          </div>
          <div class="ev-details-row">
            <span>Raspoloživo za ovu godinu:</span>
            <strong>{{ details.availableThisYear }}</strong>
          </div>
          <div class="ev-details-row ev-details-row--total">
            <span>Ukupno na raspolaganju:</span>
            <strong>{{ details.totalAvailable }}</strong>
          </div>
        </div>

        <div class="ev-details-card">
          <div class="ev-details-card-title">✅ Trenutno stanje</div>
          <div class="ev-details-row">
            <span>Iskorišćeno do sada:</span>
            <strong>{{ details.usedSoFar }}</strong>
          </div>
          <div class="ev-details-row">
            <span>Ostatak iz prošle godine:</span>
            <strong
              :class="{ 'ev-details-negative': details.restLastYear < 0 }"
              >{{ details.restLastYear }}</strong
            >
          </div>
          <div class="ev-details-row">
            <span>Ostatak ove godine:</span>
            <strong
              :class="{ 'ev-details-negative': details.restThisYear < 0 }"
              >{{ details.restThisYear }}</strong
            >
          </div>
          <div v-if="details.usedOverage > 0" class="ev-details-warning">
            ⚠️ Iskorišćeno premašuje raspoloživo za
            <strong class="ev-details-negative"
              >−{{ details.usedOverage }}</strong
            >
            dana
          </div>
        </div>

        <div class="ev-details-card ev-details-card--planned">
          <div class="ev-details-card-title">
            <span class="ev-planned-value">📅 Sa planiranim odmorima</span>
          </div>
          <div class="ev-details-row">
            <span>Planirano (preostalo godine):</span>
            <strong class="ev-planned-value">{{ details.plannedTotal }}</strong>
          </div>
          <div class="ev-details-row">
            <span>Iskorišćeno + planirano:</span>
            <strong>{{ details.usedPlusPlanned }}</strong>
          </div>
          <div class="ev-details-row ev-details-row--total">
            <span>Projektovani ostatak na kraju godine:</span>
            <strong
              :class="{ 'ev-details-negative': details.projectedRest < 0 }"
              >{{ details.projectedRest }}</strong
            >
          </div>
          <div v-if="details.plannedOverage > 0" class="ev-details-warning">
            ⚠️ Iskorišćeno + planirano premašuje raspoloživo za
            <strong class="ev-details-negative"
              >−{{ details.plannedOverage }}</strong
            >
            dana
          </div>
        </div>
      </div>

      <!-- Employee absence detail (year calendar + 12 monthly tables) -->
      <div v-if="canShowEmployeeDetail" class="ev-employee-detail">
        <div v-if="isLoadingAbsences" class="ev-detail-loading">
          <div class="ev-detail-spinner"></div>
          <p>Učitavanje detalja odsustava...</p>
        </div>

        <template v-else>
          <employee-year-calendar
            :year="year"
            :realized-absences="realizedAbsences"
            :planned-absences="futurePlannedAbsences"
          />

          <div class="ev-absence-list">
            <div class="ev-absence-list-header">
              <h4 class="ev-absence-list-title">
                📝 Lista odsustava ({{ combinedAbsences.length }})
              </h4>
              <div class="ev-absence-list-legend">
                <span class="ev-absence-badge ev-absence-badge--realized"
                  >Realizovano</span
                >
                <span class="ev-absence-badge ev-absence-badge--planned"
                  >Planirano</span
                >
              </div>
            </div>

            <div
              v-if="combinedAbsences.length === 0"
              class="ev-absence-list-empty"
            >
              Nema evidentiranih odsustava za izabranu godinu.
            </div>

            <table v-else class="ev-absence-table">
              <thead>
                <tr>
                  <th>Tip</th>
                  <th>Status</th>
                  <th>Od</th>
                  <th>Do</th>
                  <th class="ev-absence-col-num">Dana</th>
                  <th>Napomena</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(abs, idx) in combinedAbsences"
                  :key="abs.kind + '-' + (abs.employeeStatusId || idx)"
                  :class="absenceRowClass(abs)"
                >
                  <td>
                    <span
                      class="ev-absence-badge"
                      :class="
                        abs.kind === 'planned'
                          ? 'ev-absence-badge--planned'
                          : 'ev-absence-badge--realized'
                      "
                    >
                      {{ abs.kind === "planned" ? "Planirano" : "Realizovano" }}
                    </span>
                  </td>
                  <td>
                    <span class="ev-absence-code">{{
                      absenceCode(abs.statusId)
                    }}</span>
                    <span
                      v-if="abs.absenceDescription"
                      class="ev-absence-desc"
                      >{{ abs.absenceDescription }}</span
                    >
                  </td>
                  <td>{{ formatDate(abs.startDate) }}</td>
                  <td>{{ formatEndDate(abs.endDate) }}</td>
                  <td class="ev-absence-col-num">
                    {{ absenceDays(abs) }}
                  </td>
                  <td class="ev-absence-note">{{ abs.note || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ReportService from "../../services/report.service";
import EmployeeYearCalendar from "./EmployeeYearCalendar.vue";

const STATUS_CODE_MAP = {
  2: "GO",
  3: "BO",
  4: "SO",
  5: "PO",
  6: "PR",
  7: "NO",
  8: "SN",
  9: "SR",
  10: "TB",
  12: "NP",
  14: "RK",
};

// Status IDs that count as a real "absence" worth listing (excludes present,
// holidays, weekends, "not employed").
const ABSENCE_STATUS_IDS = new Set([2, 3, 4, 5, 7, 10, 14]);

function dayDiff(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return Math.round((e - s) / (1000 * 60 * 60 * 24));
}

export default {
  name: "YearlyStatusLegend",

  components: {
    EmployeeYearCalendar,
  },

  props: {
    details: {
      type: Object,
      default: null,
    },
    employeeId: {
      type: Number,
      default: 0,
    },
    year: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      isLoadingAbsences: false,
      employeeAbsenceStatuses: [],
      employeeDailyLogs: [],
      loadedKey: null,
    };
  },

  computed: {
    canShowEmployeeDetail() {
      return !!this.details && !!this.employeeId && !!this.year;
    },

    today() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },

    // Realized absences derived from daily logs: groups of consecutive days
    // (calendar days) with the same effective absence statusId.
    realizedAbsences() {
      const logs = (this.employeeDailyLogs || [])
        .filter((dl) => {
          const sid = dl.statusIdOverride || dl.statusId;
          return ABSENCE_STATUS_IDS.has(sid);
        })
        .map((dl) => ({
          date: new Date(dl.logDate),
          statusId: dl.statusIdOverride || dl.statusId,
          note: dl.comment || dl.note || "",
        }))
        .sort((a, b) => a.date - b.date);

      const ranges = [];
      let current = null;

      logs.forEach((entry) => {
        const d = new Date(entry.date);
        d.setHours(0, 0, 0, 0);

        if (
          current &&
          current.statusId === entry.statusId &&
          dayDiff(current.lastDate, d) === 1
        ) {
          current.lastDate = d;
          current.days += 1;
          if (entry.note && !current.notes.includes(entry.note)) {
            current.notes.push(entry.note);
          }
        } else {
          if (current) ranges.push(current);
          current = {
            statusId: entry.statusId,
            firstDate: d,
            lastDate: d,
            days: 1,
            notes: entry.note ? [entry.note] : [],
          };
        }
      });
      if (current) ranges.push(current);

      return ranges.map((r) => ({
        kind: "realized",
        statusId: r.statusId,
        startDate: r.firstDate.toISOString(),
        // endDate is exclusive (next day after last)
        endDate: new Date(
          r.lastDate.getFullYear(),
          r.lastDate.getMonth(),
          r.lastDate.getDate() + 1
        ).toISOString(),
        days: r.days,
        note: r.notes.join("; "),
      }));
    },

    // Planned absences: from the absence-statuses table, but only the portion
    // that lies in the future (today and later). If a planned absence has
    // already started, we still keep it because the remaining future days are
    // a real plan.
    futurePlannedAbsences() {
      const today = this.today;
      return (this.employeeAbsenceStatuses || [])
        .filter((abs) => {
          if (!ABSENCE_STATUS_IDS.has(abs.absenceStatus)) return false;
          const end = new Date(abs.endDate);
          end.setHours(0, 0, 0, 0);
          // endDate is exclusive in calendar iteration; keep if any day is >= today
          return end > today;
        })
        .map((abs) => {
          const start = new Date(abs.startDate);
          start.setHours(0, 0, 0, 0);
          // Trim start to today so we don't visualize "planned" days that are
          // actually in the past (those are reflected by realizedAbsences).
          const effectiveStart = start < today ? today : start;
          return {
            kind: "planned",
            employeeStatusId: abs.employeeStatusId,
            statusId: abs.absenceStatus,
            startDate: effectiveStart.toISOString(),
            endDate: abs.endDate,
            days: dayDiff(effectiveStart, abs.endDate),
            note: abs.note || "",
            absenceDescription: abs.absenceDescription || "",
          };
        })
        .filter((abs) => abs.days > 0);
    },

    combinedAbsences() {
      return [...this.realizedAbsences, ...this.futurePlannedAbsences].sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate)
      );
    },
  },

  watch: {
    employeeId: {
      immediate: true,
      handler() {
        this.maybeLoadAbsences();
      },
    },
    year() {
      this.maybeLoadAbsences();
    },
  },

  methods: {
    async maybeLoadAbsences() {
      if (!this.employeeId || !this.year) {
        this.employeeAbsenceStatuses = [];
        this.employeeDailyLogs = [];
        this.loadedKey = null;
        return;
      }

      const key = `${this.employeeId}_${this.year}`;
      if (key === this.loadedKey) {
        return;
      }

      this.isLoadingAbsences = true;
      try {
        const [absencesResp, logsResp] = await Promise.all([
          ReportService.GetEmployeeAbsenceStatusesByEmployeeIdAndYear(
            this.employeeId,
            this.year
          ),
          ReportService.GetDailyLogsByEmployeeIdAndYear(
            this.employeeId,
            this.year
          ),
        ]);
        this.employeeAbsenceStatuses = Array.isArray(absencesResp.data)
          ? absencesResp.data
          : [];
        this.employeeDailyLogs = Array.isArray(logsResp.data)
          ? logsResp.data
          : [];
        this.loadedKey = key;
      } catch (err) {
        console.error("Error loading employee absence/daily-log data:", err);
        this.employeeAbsenceStatuses = [];
        this.employeeDailyLogs = [];
      } finally {
        this.isLoadingAbsences = false;
      }
    },

    absenceCode(statusId) {
      return STATUS_CODE_MAP[statusId] || "??";
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      return `${day}.${month}.${d.getFullYear()}.`;
    },

    // For display: end date is exclusive in our internal representation,
    // so subtract one day to show the inclusive last day of absence.
    formatEndDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return dateStr;
      d.setDate(d.getDate() - 1);
      return this.formatDate(d.toISOString());
    },

    absenceDays(abs) {
      if (typeof abs.days === "number") return abs.days;
      const diff = dayDiff(abs.startDate, abs.endDate);
      return diff > 0 ? diff : 0;
    },

    absenceRowClass(abs) {
      return abs.kind === "planned"
        ? "ev-absence-row--planned"
        : "ev-absence-row--realized";
    },
  },
};
</script>

<style scoped>
.ev-planned-value {
  color: #b7791f;
  font-weight: 600;
}

.ev-table-legend {
  margin-top: 14px;
  padding: 12px 14px;
  background: #fffaf0;
  border: 1px solid #fbd38d;
  border-radius: 8px;
}

.ev-legend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.ev-legend-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #744210;
}

.ev-legend-hint {
  font-size: 11px;
  color: #975a16;
  font-style: italic;
}

.ev-legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.ev-legend-item {
  font-size: 12px;
  color: var(--ev-text-secondary);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ev-legend-sample {
  font-weight: 700;
}

.ev-legend-clear {
  background: transparent;
  border: 1px solid #fbd38d;
  color: #744210;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ev-legend-clear:hover {
  background: #fbd38d;
}

.ev-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
}

.ev-details-card {
  background: white;
  border: 1px solid #fbd38d;
  border-radius: 6px;
  padding: 10px 12px;
}

.ev-details-card--planned {
  border-color: #d69e2e;
  background: #fffbeb;
}

.ev-details-card-title {
  font-size: 12px;
  font-weight: 700;
  color: #744210;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ev-details-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: var(--ev-text-secondary);
  padding: 3px 0;
}

.ev-details-row--total {
  border-top: 1px dashed #fbd38d;
  margin-top: 4px;
  padding-top: 6px;
  font-weight: 700;
  color: var(--ev-text-primary);
}

.ev-details-negative {
  color: #c53030;
  font-weight: 700;
}

.ev-details-warning {
  margin-top: 8px;
  padding: 6px 8px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 4px;
  font-size: 12px;
  color: #742a2a;
}

.ev-employee-detail {
  margin-top: 14px;
}

.ev-detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #ffffff;
  border: 1px dashed #fbd38d;
  border-radius: 6px;
  color: #744210;
  font-size: 12px;
}

.ev-detail-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #fbd38d;
  border-top-color: #b7791f;
  border-radius: 50%;
  animation: ev-legend-spin 0.9s linear infinite;
  margin-bottom: 8px;
}

@keyframes ev-legend-spin {
  to {
    transform: rotate(360deg);
  }
}

.ev-absence-list {
  margin-top: 14px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.ev-absence-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.ev-absence-list-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
}

.ev-absence-list-empty {
  font-size: 12px;
  color: #718096;
  font-style: italic;
  padding: 8px 4px;
}

.ev-absence-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.ev-absence-table thead th {
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #4a5568;
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.ev-absence-table tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid #edf2f7;
  color: #2d3748;
  vertical-align: top;
}

.ev-absence-table tbody tr:last-child td {
  border-bottom: none;
}

.ev-absence-col-num {
  text-align: right;
  width: 60px;
  white-space: nowrap;
}

.ev-absence-code {
  display: inline-block;
  font-weight: 700;
  background: #edf2f7;
  color: #2d3748;
  padding: 1px 6px;
  border-radius: 4px;
  margin-right: 6px;
  font-size: 11px;
}

.ev-absence-desc {
  color: #4a5568;
}

.ev-absence-note {
  color: #718096;
  font-style: italic;
}

.ev-absence-row--realized {
  background: #ebf8ff;
}

.ev-absence-row--realized .ev-absence-code {
  background: #bee3f8;
  color: #2c5282;
}

.ev-absence-row--planned {
  background: #fffbeb;
}

.ev-absence-row--planned .ev-absence-code {
  background: #fbd38d;
  color: #744210;
}

.ev-absence-list-legend {
  display: flex;
  gap: 8px;
}

.ev-absence-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ev-absence-badge--realized {
  background: #bee3f8;
  color: #2c5282;
}

.ev-absence-badge--planned {
  background: #fbd38d;
  color: #744210;
}
</style>
